import assert from '../style-spec/util/assert';

import type {
    StructArray,
    StructArrayMember
} from '../util/struct_array';
import type Program from '../render/program';
import type Context from '../gl/context';
import type {UniformBindings} from '../render/uniform_binding';

/**
 * @enum {string} AttributeType
 * @private
 * @readonly
 */
const AttributeType = {
    Int8: 'BYTE',
    Uint8: 'UNSIGNED_BYTE',
    Int16: 'SHORT',
    Uint16: 'UNSIGNED_SHORT',
    Int32: 'INT',
    Uint32: 'UNSIGNED_INT',
    Float32: 'FLOAT'
};

/**
 * The `VertexBuffer` class turns a `StructArray` into a WebGL buffer. Each member of the StructArray's
 * Struct type is converted to a WebGL atribute.
 * @private
 */
class VertexBuffer {
    length: number;
    attributes: ReadonlyArray<StructArrayMember>;
    itemSize: number;
    dynamicDraw: boolean | null | undefined;
    context: Context;
    buffer: WebGLBuffer | null | undefined;
    instanceCount: number | null | undefined;

    /**
     * @param dynamicDraw Whether this buffer will be repeatedly updated.
     * @private
     */
    constructor(context: Context, array: StructArray, attributes: ReadonlyArray<StructArrayMember>, dynamicDraw?: boolean, noDestroy?: boolean, instanceCount?: number) {
        this.length = array.length;
        this.attributes = attributes;
        this.itemSize = array.bytesPerElement;
        this.dynamicDraw = dynamicDraw;
        this.instanceCount = instanceCount;
        this.context = context;
        const gl = context.gl;
        this.buffer = gl.createBuffer();
        context.bindVertexBuffer.set(this.buffer);
        gl.bufferData(gl.ARRAY_BUFFER, array.arrayBuffer, this.dynamicDraw ? gl.DYNAMIC_DRAW : gl.STATIC_DRAW);

        if (!this.dynamicDraw && !noDestroy) {
            array.destroy();
        }
    }

    bind() {
        this.context.bindVertexBuffer.set(this.buffer);
    }

    updateData(array: StructArray) {
        assert(array.length === this.length);
        const gl = this.context.gl;
        this.bind();
        gl.bufferSubData(gl.ARRAY_BUFFER, 0, array.uint8, 0, array.length * array.bytesPerElement);
    }

    enableAttributes(gl: WebGL2RenderingContext, program: Program<UniformBindings>) {
        for (const member of this.attributes) {
            const attribIndex = program.getAttributeLocation(gl, member.name);
            if (attribIndex !== -1) {
                gl.enableVertexAttribArray(attribIndex);
            }
        }
    }

    /**
     * Set the attribute pointers in a WebGL context.
     * @param gl The WebGL context.
     * @param program The active WebGL program.
     * @param vertexOffset Index of the starting vertex of the segment.
     */
    setVertexAttribPointers(gl: WebGL2RenderingContext, program: Program<UniformBindings>, vertexOffset?: number | null) {
        for (const member of this.attributes) {
            const attribIndex = program.getAttributeLocation(gl, member.name);
            if (attribIndex !== -1) {
                const byteOffset = member.offset + (this.itemSize * (vertexOffset || 0));
                // Floats and normalized integers are both read as floats in the shader, so they go
                // through vertexAttribPointer. Plain integer types use vertexAttribIPointer.
                if (member.type === 'Float32' || member.normalized) {
                    gl.vertexAttribPointer(
                        attribIndex,
                        member.components,
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                        gl[AttributeType[member.type]],
                        !!member.normalized,
                        this.itemSize,
                        byteOffset
                    );
                } else {
                    gl.vertexAttribIPointer(
                        attribIndex,
                        member.components,
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                        gl[AttributeType[member.type]],
                        this.itemSize,
                        byteOffset
                    );
                }
            }
        }
    }

    setVertexAttribDivisor(gl: WebGL2RenderingContext, program: Program<UniformBindings>, value: number) {
        for (let j = 0; j < this.attributes.length; j++) {
            const member = this.attributes[j];
            const attribIndex = program.getAttributeLocation(gl, member.name);
            if (attribIndex !== -1 && this.instanceCount && this.instanceCount > 0) {
                gl.vertexAttribDivisor(attribIndex, value);
            }
        }
    }

    /**
     * Destroy the GL buffer bound to the given WebGL context.
     */
    destroy() {
        const gl = this.context.gl;
        if (this.buffer) {
            gl.deleteBuffer(this.buffer);
            delete this.buffer;
        }
    }
}

export default VertexBuffer;
